import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

export const EASE = [0.16, 1, 0.3, 1];

const useEnterView = (margin = 60) => {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (show) return;
    const check = () => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight - margin) setShow(true);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [show, margin]);
  return [ref, show];
};

export const Reveal = ({ children, delay = 0, y = 28, className = "" }) => {
  const [ref, show] = useEnterView(80);
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y }}
      animate={show ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.75, ease: [0.215, 0.61, 0.355, 1], delay }}
    >
      {children}
    </motion.div>
  );
};

const MaskedLine = ({ children, delay, mode }) => {
  const [ref, show] = useEnterView(60);
  const on = mode === "load" ? true : show;
  return (
    <span ref={ref} className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={on ? { y: "0%" } : {}}
        transition={{ duration: 1.05, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
};

export const MaskedLines = ({ lines, delay = 0, mode = "load", className = "", lineClassName = "" }) => (
  <span className={`block ${className}`}>
    {lines.map((line, i) => (
      <MaskedLine key={i} delay={delay + i * 0.13} mode={mode}>
        <span className={lineClassName}>{line}</span>
      </MaskedLine>
    ))}
  </span>
);

export const ChapterLabel = ({ number, label, dark = false, testId }) => (
  <div
    data-testid={testId}
    className={`flex items-center gap-4 font-mono text-xs uppercase tracking-[0.25em] ${
      dark ? "text-[#A3A299]" : "text-muted"
    }`}
  >
    <span className="text-muted">{number}</span>
    <span className={`h-px w-10 ${dark ? "bg-darkborder" : "bg-sand"}`} aria-hidden="true" />
    <span>{label}</span>
  </div>
);
